<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { languageIdBus } from '@/event/languageIdBus'
import { productBus } from '@/event/product/productBus'
import { productIdBus } from '@/event/product/productIdBus'
import { relatedGroupBus } from '@/event/product/relatedGroupeBus'
import { showSelectedModalProductHappy } from '@/happyTheme/event/product/showSelectedModalProductHappy'
import { onHover } from '@/happyTheme/utils/onHover'
import { toggleSelectedModalProductHappy } from '@/happyTheme/utils/product/toggleSelectedModalProductHappy'
import { excludeRouteDropdownCategoryArray } from '@/storage/navbar/excludeRoute'
import { getOptionName } from '@/utils/product/getOptionName'
import { getProductDescription } from '@/utils/product/getProductDescription'
import { getProductName } from '@/utils/product/getProductName'
import { hasDescriptionForLanguage } from '@/utils/product/hasDescriptionForLanguage'
import { updateProduct } from '@/utils/product/updateProduct'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import SelectOptionProduct from './SelectOptionProductHappy.vue'
import SelectOptionProductHappy from './SelectOptionProductHappy.vue'
import type { Option } from '@/storage/products'

const route = useRoute()
const openStates = ref<boolean[]>([])
const selectOptionsList = ref<
    {
        name_option: string
        value_option: Option
    }[]
>([])

const {
    isActive: isHovered1,
    onPointerEnter: onEnter1,
    onPointerLeave: onLeave1,
    onClick: onClick1,
    onUnclick: onUnclick1
} = onHover()

const showHeader = ref(true)
let lastScrollY = 0

const handleScroll = () => {
    const currentY = window.scrollY

    if (Math.abs(currentY - lastScrollY) > 5) {
        showHeader.value = currentY < lastScrollY
        lastScrollY = currentY
    }
}

function selectedOption(option: { name_option: string; value_option: Option }) {
    const index = selectOptionsList.value.findIndex(
        (item) => item.name_option === option.name_option
    )
    if (index !== -1) {
        selectOptionsList.value[index] = option
    } else {
        selectOptionsList.value.push(option)
    }
}

function toggleShowDropdownOption(index: number) {
    openStates.value[index] = !openStates.value[index]
}

watch(
    () => productBus.value?.options,
    (opts) => {
        if (!opts) return
        openStates.value = opts.map(() => false)
    },
    { immediate: true }
)

watch(
    () => route.name,
    (routeName) => {
        if (routeName) {
            const routeNameString = String(routeName)
            if (!excludeRouteDropdownCategoryArray.includes(routeNameString)) {
                showSelectedModalProductHappy.value = false
            }
        }
    },
    {
        deep: true
    }
)

onMounted(() => {
    lastScrollY = window.scrollY
    window.addEventListener('scroll', handleScroll)
})
onBeforeUnmount(() => window.removeEventListener('scroll', handleScroll))
</script>

<template>
    <section
        v-if="showSelectedModalProductHappy && productBus"
        class="section-modal-product-selected-happy"
        :class="{
            '-translate-y-1': !showHeader,
            'translate-y-[4.9rem]': showHeader
        }"
    >
        <section
            @click.stop="toggleSelectedModalProductHappy"
            v-if="showSelectedModalProductHappy && productBus"
            class="overlay-section-product-happy"
        ></section>

        <section class="section-selected-product-happy">
            <div class="div-button-close-product-selected-happy">
                <button
                    @mouseenter="onEnter1"
                    @mouseleave="onLeave1"
                    @pointerdown="onClick1"
                    @pointerup="onUnclick1"
                    class="close-modal-product-selected-happy"
                    @click="toggleSelectedModalProductHappy"
                >
                    <svg
                        class="svg-cross-product-selected-happy"
                        :class="{
                            'stroke-border-red-happy-light': isHovered1,
                            'happy-light:stroke-text-dark-happy-light happy-dark:stroke-bg-happy-light':
                                !isHovered1
                        }"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                    >
                        <path
                            d="M16 8L8 16M8.00001 8L16 16"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
            </div>

            <section class="modal-body-product-selected-happy">
                <section class="section-product-selected-happy">
                    <div class="div-product-selected-happy">
                        <img
                            class="img-product-selected-happy"
                            :src="`${URL_IMAGE}${productBus.image}`"
                            alt=""
                        />
                    </div>

                    <div class="div-product-information-selected-happy">
                        <h3 class="h3-product-selected-happy">
                            {{ getProductName(productBus) }}
                        </h3>

                        <p
                            v-if="
                                hasDescriptionForLanguage(
                                    productBus.description,
                                    languageIdBus
                                )
                            "
                        >
                            {{ getProductDescription(productBus) }}
                        </p>
                    </div>
                </section>

                <section
                    class="section-product-option-selected-happy"
                    v-if="productBus.options.length > 0"
                >
                    <div class="div-product-option-information-happy">
                        <h4 class="h4-product-option-selected-happy">Options</h4>

                        <p class="text-obligation-option-selected-happy">
                            <span class="text-sm">*</span>Obligatoire
                        </p>
                    </div>
                    <section class="div-form-product-happy">
                        <div
                            v-for="(option, idx) in productBus.options"
                            :key="option.option_id"
                            class="div-v-for-option-product-happy"
                        >
                            <div
                                v-if="!relatedGroupBus.includes(option.option_id)"
                                class="form-option-title-product-happy"
                                @click.stop="toggleShowDropdownOption(idx)"
                            >
                                <h4
                                    :id="getOptionName(option, languageIdBus)"
                                    v-if="option.required && option.type === 'select'"
                                    class="h4-option-happy"
                                >
                                    <p class="">
                                        {{ getOptionName(option, languageIdBus) }}
                                    </p>

                                    <p
                                        v-if="
                                            option.free_option_count > 0 &&
                                            option.max_option_count == 0
                                        "
                                        class="choice"
                                    >
                                        <span class="choice"> *</span>
                                        <!-- ({{ option.free_option_count + ' choix' }}) -->
                                    </p>

                                    <div
                                        v-else-if="
                                            option.free_option_count > 0 &&
                                            option.max_option_count > 0
                                        "
                                        class="choice"
                                    >
                                        <span class="choice"> *</span>
                                        <!-- (
                                        <p v-if="Number(option.free_option_count) !== 0">
                                            {{ option.free_option_count }} choix gratuit
                                        </p>
                                        <span
                                            v-if="
                                                option.max_option_count - option.free_option_count >
                                                0
                                            "
                                        >
                                            +
                                            {{ option.max_option_count - option.free_option_count }}
                                            choix payant{{
                                                option.max_option_count - option.free_option_count >
                                                1
                                                    ? 's'
                                                    : ''
                                            }}</span
                                        >) -->
                                    </div>
                                    <p
                                        v-if="
                                            option.required &&
                                            option.free_option_count == 0 &&
                                            option.max_option_count == 0
                                        "
                                        class="choice"
                                    >
                                        <span class="choice"> *</span>
                                    </p>
                                </h4>

                                <h4
                                    v-else-if="
                                        option.type === 'radio' && !option.required
                                    "
                                    :id="getOptionName(option, languageIdBus)"
                                    class="h4-option-happy"
                                >
                                    {{ getOptionName(option, languageIdBus) }}
                                    <!-- <span class="choice-radio">(1 choix possible)</span> -->
                                </h4>

                                <h4
                                    v-else-if="option.type === 'radio' && option.required"
                                    :id="getOptionName(option, languageIdBus)"
                                    class="h4-option-happy"
                                >
                                    {{ getOptionName(option, languageIdBus) }}
                                    <span class="choice">*</span>
                                </h4>

                                <h4
                                    v-else
                                    :id="getOptionName(option, languageIdBus)"
                                    class="h4-option-happy"
                                >
                                    {{ getOptionName(option, languageIdBus) }}
                                </h4>

                                <svg
                                    class="svg-arrow-happy-option-product-happy"
                                    :class="{
                                        'rotate-180': openStates[idx],
                                        'pb-0.5': !openStates[idx]
                                    }"
                                    viewBox="0 0 16 10"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M15.6994 0.852272C15.2985 0.45142 14.6486 0.45142 14.2478 0.852272L8.13076 6.96921L2.01369 0.852272C1.61284 0.45142 0.963042 0.45142 0.56219 0.852272C0.161338 1.25312 0.161338 1.90292 0.56219 2.30377L7.40501 9.14663C7.59743 9.33912 7.85855 9.44727 8.13076 9.44727C8.40297 9.44727 8.66409 9.33912 8.85651 9.14663L15.6994 2.30377C16.1002 1.90292 16.1002 1.25312 15.6994 0.852272Z"
                                    />
                                </svg>
                            </div>
                            <SelectOptionProductHappy
                                v-if="openStates[idx]"
                                :options="option"
                                :name_option="getOptionName(option, languageIdBus)"
                                @select-option="(opt) => selectedOption(opt)"
                            />
                        </div>
                    </section>
                </section>
            </section>
        </section>
    </section>
</template>

<style scoped>
.section-modal-product-selected-happy {
    @apply z-20 fixed top-0 w-screen transition-transform duration-300 flex justify-end;
}

.overlay-section-product-happy {
    @apply fixed top-0 w-screen h-screen transition-opacity duration-300 bg-text-dark-happy-light opacity-70;
}

.section-selected-product-happy {
    @apply relative top-0 w-full h-screen happy-light:bg-bg-happy-light happy-dark:bg-text-dark-happy-light;
    @apply flex flex-col flex-1 z-10;
}

.div-button-close-product-selected-happy {
    @apply w-full flex p-2;
}

.svg-cross-product-selected-happy {
    @apply w-10;
}

.modal-body-product-selected-happy {
    @apply flex-1 overflow-y-auto;
}

.section-product-selected-happy {
    @apply w-full flex flex-col p-2;
}

.div-product-selected-happy {
    @apply w-full flex flex-col items-center gap-2 mb-2;
}

.img-product-selected-happy {
    @apply max-w-80;
}

.div-product-information-selected-happy {
    @apply w-full flex flex-col gap-2;
}

.h3-product-selected-happy {
    @apply font-bold text-3xl;
}

.div-product-option-selected-happy {
    @apply w-full;
}
.section-product-option-selected-happy {
    @apply w-full flex flex-col p-2 pb-14 bg-border-gray-happy-light;
}

.div-product-option-information-happy {
    @apply w-full flex justify-between items-center;
}

.h4-product-option-selected-happy {
    @apply font-semibold text-lg pb-2 happy-dark:text-text-dark-happy-light;
}

.text-obligation-option-selected-happy {
    @apply text-xs text-border-red-happy-light font-semibold;
}

.div-form-product-happy {
    @apply flex flex-col gap-3;
}

.div-v-for-option-product-happy {
    @apply rounded-xl   happy-light:bg-bg-happy-light happy-dark:bg-text-dark-happy-light w-full;
}

.form-option-title-product-happy {
    @apply flex flex-row items-center justify-between p-3 border-b border-border-gray-happy-light;
}

.h4-option-happy {
    @apply flex font-semibold whitespace-pre-wrap;
}
.choice {
    @apply text-border-red-happy-light;
}

.svg-arrow-happy-option-product-happy {
    @apply w-5 h-5 happy-light:fill-text-dark-happy-light happy-dark:fill-bg-happy-light transition-transform duration-300;
}

@media screen and (min-width: 480px) {
    .section-selected-product-happy {
        @apply max-w-[26rem] h-screen;
    }
}
</style>
